/**
 * Detailed view of a single security finding including CVE info,
 * evidence, remediation guidance, and status management.
 */

import SeverityBadge from '@/components/common/SeverityBadge';
import { formatDate, formatCvss } from '@/utils/formatters';
import type { Finding } from '@/types/finding';

interface FindingDetailProps {
  finding: Finding;
}

export default function FindingDetail({ finding }: FindingDetailProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{finding.title}</h2>
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500">
            <SeverityBadge severity={finding.severity} />
            {finding.cve_id && (
              <span className="font-mono">{finding.cve_id}</span>
            )}
            {finding.cwe_id && (
              <span className="font-mono text-gray-400">{finding.cwe_id}</span>
            )}
          </div>
        </div>
        <div className="text-right text-sm text-gray-500">
          <p>CVSS: <span className="font-semibold">{formatCvss(finding.cvss_score)}</span></p>
          <p>Found: {formatDate(finding.created_at)}</p>
        </div>
      </div>

      {/* Description */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700">Description</h3>
        <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{finding.description}</p>
      </div>

      {/* Evidence */}
      {finding.evidence && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Evidence</h3>
          <pre className="mt-1 overflow-x-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700">
            {finding.evidence}
          </pre>
        </div>
      )}

      {/* Remediation */}
      {finding.remediation && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Remediation</h3>
          <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{finding.remediation}</p>
        </div>
      )}

      {finding.host_details && (finding.host_details.os || finding.host_details.device_type || finding.host_details.mac) && (
        <section aria-labelledby="finding-device" className="rounded-md border border-gray-200 p-4 text-sm">
          <h3 id="finding-device" className="mb-2 text-sm font-semibold text-gray-700">Device</h3>
          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
            {finding.host_details.os && <><dt className="text-gray-500">Operating system</dt><dd className="text-gray-800">{finding.host_details.os}{finding.host_details.os_accuracy != null && ` (${finding.host_details.os_accuracy}% match)`}</dd></>}
            {finding.host_details.device_type && <><dt className="text-gray-500">Device type</dt><dd className="text-gray-800">{finding.host_details.device_type}</dd></>}
            {finding.host_details.ip && <><dt className="text-gray-500">IP address</dt><dd className="font-mono text-gray-800">{finding.host_details.ip}</dd></>}
            {finding.host_details.names.length > 0 && <><dt className="text-gray-500">Names</dt><dd className="font-mono text-gray-800">{finding.host_details.names.join(', ')}</dd></>}
            {finding.host_details.mac && <><dt className="text-gray-500">MAC address</dt><dd className="font-mono text-gray-800">{finding.host_details.mac}{finding.host_details.mac_vendor && ` (${finding.host_details.mac_vendor})`}</dd></>}
          </dl>
          <p className="mt-2 text-xs text-gray-500">Identified from network fingerprinting; the operating system is a best match, not a confirmed inventory record.</p>
        </section>
      )}

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-4 rounded-md bg-gray-50 p-4 text-sm">
        {(finding.host || finding.asset) && (
          <div className="col-span-2">
            <span className="font-medium text-gray-500">Host:</span>{' '}
            <span className="font-mono text-gray-700">
              {finding.host ?? finding.asset?.label ?? finding.asset?.value ?? '—'}
            </span>
            {finding.host && finding.asset && (
              <span className="ml-2 text-gray-400">
                ({finding.asset.label ?? finding.asset.value})
              </span>
            )}
          </div>
        )}
        <div>
          <span className="font-medium text-gray-500">Scanner:</span>{' '}
          <span className="text-gray-700">{finding.scanner_engine}</span>
        </div>
        <div>
          <span className="font-medium text-gray-500">Status:</span>{' '}
          <span className="text-gray-700 capitalize">{finding.status.replace('_', ' ')}</span>
        </div>
        {finding.resolved_at && (
          <div>
            <span className="font-medium text-gray-500">Resolved:</span>{' '}
            <span className="text-gray-700">{formatDate(finding.resolved_at)}</span>
          </div>
        )}
        {finding.verified_at && (
          <div>
            <span className="font-medium text-gray-500">Verified:</span>{' '}
            <span className="text-gray-700">{formatDate(finding.verified_at)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
